import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAuthStore } from '../../stores/authStore';
import {
  DndContext,
  closestCenter,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { cuesAPI, vmixInstancesAPI } from '../../lib/api';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useRundownRows } from '../../hooks/useRundownRows';
import CueRow from './CueRow';
import { RUNDOWN_KEYBOARD_CODES } from './dragHandle';

// ── Column definitions ────────────────────────────────────────────────────────
// fixed  = true  → always exactly defaultWidth, not resizable (clock/badge cols)
// fixed  = false → resizable by user; participates in auto-fill
// align  = 'center' | 'left' — applied to both header text and cell content
const CLOCK_W = 100; // px — wide enough for "HH:MM:SS" at text-sm monospace

const COLUMN_DEFS = [
  { key: 'type',            label: 'Type',             header: 'Type',       defaultWidth: 100,    fixed: true,  align: 'center' },
  { key: 'in',              label: 'In Time',           header: 'In',         defaultWidth: CLOCK_W, fixed: true,  align: 'center' },
  { key: 'duration',        label: 'Duration',          header: 'Dur',        defaultWidth: CLOCK_W, fixed: true,  align: 'center' },
  { key: 'out',             label: 'Out Time',          header: 'Out',        defaultWidth: CLOCK_W, fixed: true,  align: 'center' },
  { key: 'plusMinus',       label: '+/- Offset',        header: '+/-',        defaultWidth: CLOCK_W, fixed: true,  align: 'center' },
  { key: 'script',          label: 'Script',            header: 'Script',     defaultWidth: 240,    fixed: false, align: 'left'   },
  { key: 'topRightLogo',    label: 'Top Right Logo',    header: 'TR Logo',    defaultWidth: 150,    fixed: false, align: 'left'   },
  { key: 'bottomRightLogo', label: 'Bottom Right Logo', header: 'BR Logo',    defaultWidth: 150,    fixed: false, align: 'left'   },
  { key: 'otherGraphics',   label: 'Other Graphics',    header: 'Other GFX',  defaultWidth: 140,    fixed: false, align: 'left'   },
  { key: 'notes',           label: 'Notes',             header: 'Notes',      defaultWidth: 140,    fixed: false, align: 'left'   },
];

// Toggles for the fixed leading cells, which sit left of the resizable columns
// and so can't live in COLUMN_DEFS (that array drives the column render loop).
// They still belong in the same show/hide menu — from the operator's side these
// are columns like any other.
const LEADING_TOGGLES = [
  {
    key: 'editControls',
    label: 'Edit controls',
    title: 'Drag handle, expand arrow, enable/clone/delete and the selection tickbox',
  },
  { key: 'status', label: 'Status', title: 'The ON AIR / NEXT / done markers' },
];

const DEFAULT_WIDTHS = Object.fromEntries(COLUMN_DEFS.map(c => [c.key, c.defaultWidth]));
const DEFAULT_VISIBLE = {
  ...Object.fromEntries(COLUMN_DEFS.map(c => [c.key, true])),
  ...Object.fromEntries(LEADING_TOGGLES.map(c => [c.key, true])),
};

// ── Per-user column prefs (visibility + custom widths) ────────────────────────
function useColumnPrefs() {
  const userId = useAuthStore.getState().user?.id || 'default';
  const storageKey = `liverundown_colprefs_${userId}`;

  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Migrate old flat-visibility format
        if (!parsed.visible && !parsed.widths) {
          return { visible: { ...DEFAULT_VISIBLE, ...parsed }, widths: null };
        }
        return {
          visible: { ...DEFAULT_VISIBLE, ...(parsed.visible || {}) },
          widths: parsed.widths || null,
        };
      }
    } catch {}
    return { visible: { ...DEFAULT_VISIBLE }, widths: null };
  });

  function save(next) {
    setPrefs(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch {}
  }

  function toggleCol(colKey) {
    save({ ...prefs, visible: { ...prefs.visible, [colKey]: !prefs.visible[colKey] } });
  }

  function setColWidth(colKey, width) {
    const base = prefs.widths || { ...DEFAULT_WIDTHS };
    save({ ...prefs, widths: { ...base, [colKey]: width } });
  }

  function resetWidths() {
    save({ ...prefs, widths: null });
  }

  return { visible: prefs.visible, customWidths: prefs.widths, toggleCol, setColWidth, resetWidths };
}

// ── Resize handle ─────────────────────────────────────────────────────────────
function ResizeHandle({ colKey, currentWidth, onResize }) {
  function onMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = currentWidth;
    function onMove(ev) {
      onResize(colKey, Math.max(40, startW + (ev.clientX - startX)));
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }
  return (
    <div
      onMouseDown={onMouseDown}
      onClick={e => e.stopPropagation()}
      className="absolute right-0 top-0 w-2 h-full cursor-col-resize bg-app-border hover:bg-blue-400 z-10 select-none transition-colors"
      title="Drag to resize column"
    />
  );
}

// ── Main component ────────────────────────────────────────────────────────────
// Fixed-width overhead that is never part of the resizable columns (px).
// Split by toggle group so hiding a leading cell hands its width back to the
// resizable columns instead of leaving a gap. All three together still sum to
// the original 444, so the default layout is unchanged.
const OVERHEAD_CORE = 48 + 16 + 96;                  // order + colorbar + LEDs/loop/test
const OVERHEAD_EDIT_CONTROLS = 36 + 32 + 32 + 80 + 24; // spacer + drag + expand + en/clone/del + checkbox
const OVERHEAD_STATUS = 80;

function fixedOverhead(visible) {
  return OVERHEAD_CORE
    + (visible.editControls !== false ? OVERHEAD_EDIT_CONTROLS : 0)
    + (visible.status !== false ? OVERHEAD_STATUS : 0);
}

function pad2(n) { return String(n).padStart(2, '0'); }
function fmtRemaining(secs) {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

export default function RundownTable({ showId, readOnly = false, textOnly = false, onAddCue, onInsertBelowSelection, canInsertBelow = false, onOpenCueBlocks, onInsertAfter, selectedIds = new Set(), onToggleSelect, onSelectAll, onDeleteSelected, onClearSelection }) {
  const cues = useShowStore(s => s.cues);
  const show = useShowStore(s => s.show);
  const [bottomAddOpen, setBottomAddOpen] = useState(false);

  // Load the workspace's vMix instances into the connection store so the
  // cue-action editor's target dropdown is populated in EVERY view that hosts
  // this table (Producer, TD, Graphics, the editor), not just RundownEditor.
  // On failure we leave any already-loaded list intact rather than clearing it.
  useEffect(() => {
    let cancelled = false;
    vmixInstancesAPI.list()
      .then(r => { if (!cancelled) useConnectionStore.getState().setVmixInstances(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showId]);

  const { reorderCues, updateCue, incrementPendingOps, decrementPendingOps, toggleGroupCollapse, collapseAllGroups } = useShowStore.getState();

  // Shared rundown derivation — the SAME hook the modern spine consumes, so
  // status / in-times / on-air / next / group structure never fork between views.
  const {
    displayedCues,
    childrenByParent,
    cueNumbers,
    groupDurations,
    onAirCue,
    nextCue,
    inTimes,
    showStartIdx,
    collapsedGroups,
    collapsingGroups,
    expandingGroups,
  } = useRundownRows(show);

  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overZone, setOverZone] = useState(null); // 'top' | 'middle' | 'bottom' | null
  const [showColMenu, setShowColMenu] = useState(false);
  // Expanded action-panel rows, lifted out of CueRow so the state survives the
  // virtualiser unmounting a row as it scrolls out of the window — otherwise a
  // tall expanded row collapses while you scroll through it.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const colMenuRef = useRef(null);
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const toggleExpand = useCallback((cueId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(cueId)) next.delete(cueId); else next.add(cueId);
      return next;
    });
  }, []);

  // Virtualiser — renders only rows in/near the viewport
  // During drag we expand overscan to the full list so dnd-kit collision detection
  // can measure all sortable items; outside of drag only a small window is mounted.
  const virtualizer = useVirtualizer({
    count: displayedCues.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 44,
    overscan: activeId ? displayedCues.length : 5,
    scrollPaddingStart: 40, // account for 40px sticky header
  });

  const { visible, customWidths, toggleCol, setColWidth, resetWidths } = useColumnPrefs();


  // Auto-scroll to on-air cue when it changes — unless the operator has turned
  // off "follow live" (so they can edit elsewhere without the view jumping back
  // when someone else takes a cue). Read at cue-change time via the live store.
  useEffect(() => {
    if (!onAirCue) return;
    if (!useSettingsStore.getState().followOnAir) return;
    const idx = displayedCues.findIndex(c => c.id === onAirCue.id);
    if (idx !== -1) {
      virtualizer.scrollToIndex(idx, { align: 'center', behavior: 'smooth' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAirCue?.id]);

  // Measure container width for auto-fill
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Close column menu on outside click
  useEffect(() => {
    if (!showColMenu) return;
    function handler(e) {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColMenu]);

  // Spring-load: auto-expand a collapsed GROUP after hovering over it for 500ms.
  // Deps are intentionally limited to [overId, overZone] so WS cue updates don't reset the timer.
  useEffect(() => {
    if (!overId || overZone !== 'middle') return;
    const { cues: currentCues, collapsedGroups: currentCollapsed } = useShowStore.getState();
    // Never spring-load while dragging a GROUP: expanding a group mid-drag
    // splits the dragged group from its own children and invites accidental
    // nesting. Groups are moved as an atomic, collapsed unit.
    const activeCue = currentCues.find(c => c.id === activeId);
    if (activeCue?.cue_type === 'GROUP') return;
    const overCue = currentCues.find(c => c.id === overId);
    if (!overCue || overCue.cue_type !== 'GROUP' || !currentCollapsed.has(overId)) return;
    const timer = setTimeout(() => toggleGroupCollapse(overId), 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overId, overZone]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: RUNDOWN_KEYBOARD_CODES })
  );

  // Custom collision detection. The GROUP "drop into" target is rendered as
  // a separate non-sortable droppable (id `${cue.id}:inner`) inside each
  // GROUP row's middle 50%. If the cursor is over that inner droppable, we
  // ALWAYS prefer it — that gives a stable, non-shifting target that matches
  // the user's mental model and isn't broken by sortable animations.
  // Otherwise: pointerWithin picks the sortable row genuinely under the
  // cursor; fallbacks handle gaps between rows.
  const cursorAwareCollision = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const innerHit = pointerCollisions.find(c =>
      typeof c.id === 'string' && c.id.endsWith(':inner')
    );
    if (innerHit) return [innerHit];
    if (pointerCollisions.length > 0) return pointerCollisions;
    const intersectionCollisions = rectIntersection(args);
    if (intersectionCollisions.length > 0) return intersectionCollisions;
    return closestCenter(args);
  }, []);

  // Resolve a possibly-suffixed over id (e.g. "abc123:inner") to the cue id
  // and a flag indicating whether the over was the inner droppable.
  function resolveOver(rawId) {
    if (typeof rawId !== 'string') return { id: rawId, isInner: false };
    if (rawId.endsWith(':inner')) {
      return { id: rawId.slice(0, -':inner'.length), isInner: true };
    }
    return { id: rawId, isInner: false };
  }

  function computeZone(activeRect, overRect, cursorY) {
    if (!overRect || overRect.height === 0) return null;
    // Prefer the live cursor Y when we have it. With verticalListSortingStrategy
    // + closestCenter, the dragged item's geometric centre drifts past the over
    // row as the list animates to make space — for an empty GROUP this puts
    // the centre permanently outside the middle zone (always 'bottom'), which
    // made dropping into a fresh GROUP impossible. The cursor position matches
    // the user's actual intent and isn't affected by sortable animations.
    const yRef = (typeof cursorY === 'number')
      ? cursorY
      : (activeRect ? activeRect.top + activeRect.height / 2 : null);
    if (yRef == null) return null;
    const relativeY = (yRef - overRect.top) / overRect.height;
    if (relativeY < 0.25) return 'top';
    if (relativeY > 0.75) return 'bottom';
    return 'middle';
  }

  function pointerYFromEvent(event) {
    const ev = event.activatorEvent;
    const startY = (ev && typeof ev.clientY === 'number') ? ev.clientY : null;
    const dy = event.delta?.y ?? 0;
    return startY != null ? startY + dy : null;
  }

  const handleDragStart = useCallback((event) => {
    const id = event.active.id;
    setActiveId(id);
    setOverId(null);
    setOverZone(null);
    // A GROUP moves as a unit. Collapse every group up front whenever the drag
    // involves any group — the dragged cue itself, or a multi-selection that
    // contains one — so each group's children travel with it (reinjected after
    // their parent at drop, never left behind by a mid-drag expansion) and no
    // group can be dropped inside another.
    const cs = useShowStore.getState().cues;
    const draggedCue = cs.find(c => c.id === id);
    const multiHasGroup = selectedIds.size > 1 && selectedIds.has(id) &&
      cs.some(c => selectedIds.has(c.id) && c.cue_type === 'GROUP');
    if (draggedCue?.cue_type === 'GROUP' || multiHasGroup) {
      setExpandedIds(new Set());
      useShowStore.getState().collapseAllGroups();
    }
  }, [selectedIds]);

  const handleDragOver = useCallback((event) => {
    const { over, active } = event;
    const resolved = over ? resolveOver(over.id) : null;
    setOverId(resolved ? resolved.id : null);
    if (resolved?.isInner) {
      // Cursor is over the GROUP middle drop target — treat as middle zone
      // unconditionally for the visual highlight + spring-load expand.
      setOverZone('middle');
      return;
    }
    const activeRect = active.rect.current.translated ?? active.rect.current.initial;
    const cursorY = pointerYFromEvent(event);
    const zone = over ? computeZone(activeRect, over.rect, cursorY) : null;
    setOverZone(zone);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);
    setOverZone(null);
    if (!over) return;
    const { id: overCueId, isInner } = resolveOver(over.id);
    if (active.id === overCueId) return;

    // Inner droppable always means "drop into this group" regardless of
    // cursor zone — the inner is only rendered for GROUP rows.
    const dropZone = isInner
      ? 'middle'
      : computeZone(
          active.rect.current.translated ?? active.rect.current.initial,
          over.rect,
          pointerYFromEvent(event),
        );

    const activeCue = cues.find(c => c.id === active.id);
    const overCue = cues.find(c => c.id === overCueId);

    // ── Drop onto a GROUP row → make dragged cue(s) children ─────────────
    // Only fires for the middle zone (50% of row height); top/bottom zones fall through to sort logic.
    // Also excludes a child of this group dragging past its own parent row to escape.
    if (overCue?.cue_type === 'GROUP' && activeCue?.cue_type !== 'GROUP' && activeCue?.parent_id !== overCue?.id && dropZone === 'middle') {
      const { updateCue } = useShowStore.getState();
      const isMultiDrop = selectedIds.size > 1 && selectedIds.has(active.id);

      // Collect eligible cues: not GROUP themselves, not already in this group.
      // Only VISIBLE selected cues move — never cues hidden inside a collapsed
      // group (otherwise a stale selection gets dumped into this group).
      const cuesToAdd = isMultiDrop
        ? displayedCues.filter(c => selectedIds.has(c.id) && c.cue_type !== 'GROUP' && c.parent_id !== overCueId)
        : (activeCue?.cue_type !== 'GROUP' && activeCue?.parent_id !== overCueId ? [activeCue] : []);

      if (cuesToAdd.length === 0) return;

      const addIds = new Set(cuesToAdd.map(c => c.id));

      // Build new order: remove cues-to-add, insert them after last existing child of target group
      const existingChildren = (childrenByParent.get(overCueId) || [])
        .filter(c => !addIds.has(c.id))
        .sort((a, b) => a.order - b.order);
      const anchorId = existingChildren.length > 0
        ? existingChildren[existingChildren.length - 1].id
        : overCueId;

      const without = cues.filter(c => !addIds.has(c.id));
      const anchorIdx = without.findIndex(c => c.id === anchorId);
      const orderedIds = [
        ...without.slice(0, anchorIdx + 1).map(c => c.id),
        ...cuesToAdd.map(c => c.id),
        ...without.slice(anchorIdx + 1).map(c => c.id),
      ];

      // Optimistic update
      reorderCues(orderedIds);
      cuesToAdd.forEach(c => updateCue({ ...c, parent_id: overCueId }));

      incrementPendingOps();
      try {
        await Promise.all(cuesToAdd.map(c => cuesAPI.update(showId, c.id, { parent_id: overCueId })));
        await cuesAPI.reorder(showId, orderedIds);
      } catch (err) {
        console.error('Drop onto group failed:', err);
        reorderCues(cues.map(c => c.id));
        cuesToAdd.forEach(c => updateCue({ ...c }));
      } finally {
        decrementPendingOps();
      }
      // Moving cues into a group completes the action — reset the selection so
      // the next selection starts clean (prevents stale-selection surprises).
      onClearSelection?.();
      return;
    }

    const oldIndex = displayedCues.findIndex(c => c.id === active.id);
    let newIndex = displayedCues.findIndex(c => c.id === overCueId);
    if (oldIndex === -1 || newIndex === -1) return;
    const isGroupDrag = activeCue?.cue_type === 'GROUP';
    // A multi-selection (which may include one or more groups) is handled by the
    // multi-select branch below — every group in it is collapsed by
    // handleDragStart, so its children ride along via the hidden-children
    // reinjection. Only a lone group falls to the single-group branch.
    const isMultiDrag = selectedIds.size > 1 && selectedIds.has(active.id);

    let displayedReordered;

    if (isGroupDrag && !isMultiDrag) {
      // GROUP drag: move the parent and all its children as a single unit
      const groupChildren = childrenByParent.get(active.id) || [];
      const groupMemberIds = new Set([active.id, ...groupChildren.map(c => c.id)]);
      const isMovingDown = newIndex > oldIndex;
      const groupMembers = displayedCues.filter(c => groupMemberIds.has(c.id));
      const nonMembers = displayedCues.filter(c => !groupMemberIds.has(c.id));

      let insertIdx;
      if (groupMemberIds.has(overCueId)) {
        if (isMovingDown) {
          const after = displayedCues.slice(newIndex + 1).find(c => !groupMemberIds.has(c.id));
          insertIdx = after ? nonMembers.findIndex(c => c.id === after.id) : nonMembers.length;
        } else {
          const before = [...displayedCues.slice(0, newIndex)].reverse().find(c => !groupMemberIds.has(c.id));
          insertIdx = before ? nonMembers.findIndex(c => c.id === before.id) + 1 : 0;
        }
      } else {
        insertIdx = nonMembers.findIndex(c => c.id === overCueId);
        if (isMovingDown) insertIdx += 1;
      }

      displayedReordered = [
        ...nonMembers.slice(0, insertIdx),
        ...groupMembers,
        ...nonMembers.slice(insertIdx),
      ];
    } else if (isMultiDrag) {
      // Multi-select drag: move all selected cues (including whole groups —
      // their collapsed children are reinjected after each parent) as one block
      const isMovingDown = newIndex > oldIndex;
      const selectedGroup = displayedCues.filter(c => selectedIds.has(c.id));
      const nonSelected = displayedCues.filter(c => !selectedIds.has(c.id));

      let insertIdx;
      if (selectedIds.has(overCueId)) {
        if (isMovingDown) {
          const after = displayedCues.slice(newIndex + 1).find(c => !selectedIds.has(c.id));
          insertIdx = after ? nonSelected.findIndex(c => c.id === after.id) : nonSelected.length;
        } else {
          const before = [...displayedCues.slice(0, newIndex)].reverse().find(c => !selectedIds.has(c.id));
          insertIdx = before ? nonSelected.findIndex(c => c.id === before.id) + 1 : 0;
        }
      } else {
        insertIdx = nonSelected.findIndex(c => c.id === overCueId);
        if (isMovingDown) insertIdx += 1;
        // GROUP top zone moving down: place before GROUP, not after
        if (overCue?.cue_type === 'GROUP' && dropZone === 'top' && isMovingDown) {
          insertIdx = nonSelected.findIndex(c => c.id === overCue.id);
        }
      }

      // GROUP bottom zone: insert after last visible child, not at the GROUP row itself
      if (overCue?.cue_type === 'GROUP' && dropZone === 'bottom') {
        const visibleChildren = cues.filter(c => c.parent_id === overCue.id && !selectedIds.has(c.id));
        if (visibleChildren.length > 0) {
          const lastChild = visibleChildren[visibleChildren.length - 1];
          const altIdx = nonSelected.findIndex(c => c.id === lastChild.id);
          if (altIdx !== -1) insertIdx = altIdx + 1;
        } else {
          // Collapsed group: insert one past the GROUP row
          const groupIdx = nonSelected.findIndex(c => c.id === overCue.id);
          if (groupIdx !== -1) insertIdx = groupIdx + 1;
        }
      }

      displayedReordered = [
        ...nonSelected.slice(0, insertIdx),
        ...selectedGroup,
        ...nonSelected.slice(insertIdx),
      ];
    } else {
      // Absolute-bottom escape: active is the last child at the very end of the list.
      // No reorder needed — cue stays at its position but parent_id is cleared below.
      const isAbsoluteBottomEscape =
        activeCue?.parent_id &&
        dropZone === 'bottom' &&
        displayedCues[displayedCues.length - 1]?.id === activeCue?.id &&
        overCue?.parent_id === activeCue?.parent_id;

      if (!isAbsoluteBottomEscape) {
        if (overCue?.cue_type === 'GROUP' && dropZone === 'bottom') {
          // GROUP bottom zone: insert after last visible child (or one past GROUP for collapsed)
          const visibleChildren = displayedCues.filter(c => c.parent_id === overCue.id);
          if (visibleChildren.length > 0) {
            const lastChildIdx = displayedCues.findIndex(c => c.id === visibleChildren[visibleChildren.length - 1].id);
            if (lastChildIdx !== -1) newIndex = lastChildIdx;
          } else {
            newIndex = newIndex + 1;
          }
        } else if (overCue?.cue_type === 'GROUP' && dropZone === 'top' && oldIndex < newIndex) {
          // GROUP top zone moving down: place before GROUP (arrayMove inserts after without this)
          newIndex = newIndex - 1;
        }
      }
      displayedReordered = isAbsoluteBottomEscape ? [...displayedCues] : arrayMove(displayedCues, oldIndex, newIndex);
    }

    // Expand displayedReordered: inject hidden children (collapsed groups) after their parent
    const displayedIdSet = new Set(displayedCues.map(c => c.id));
    const hiddenChildrenMap = new Map();
    cues.forEach(c => {
      if (c.parent_id && !displayedIdSet.has(c.id)) {
        if (!hiddenChildrenMap.has(c.parent_id)) hiddenChildrenMap.set(c.parent_id, []);
        hiddenChildrenMap.get(c.parent_id).push(c);
      }
    });
    hiddenChildrenMap.forEach(arr => arr.sort((a, b) => a.order - b.order));

    const orderedIds = [];
    displayedReordered.forEach(c => {
      orderedIds.push(c.id);
      const hidden = hiddenChildrenMap.get(c.id) || [];
      hidden.forEach(k => orderedIds.push(k.id));
    });

    // ── Detect group membership changes ─────────────────────────────────────
    // When a cue is dropped onto/between child rows, it should join that group.
    // When dropped outside all groups, it should leave its current group.
    // Exception: bottom zone of the last child in a group escapes the group.
    // Also covers the case where the active cue IS the last child (over resolves to second-to-last).
    const groupChildren = overCue?.parent_id ? (childrenByParent.get(overCue.parent_id) || []) : [];
    const lastGroupChild = groupChildren.at(-1);
    const isLastChildBottomEscape =
      overCue?.parent_id &&
      dropZone === 'bottom' &&
      (
        lastGroupChild?.id === overCue?.id ||
        // Active IS the last child and is at the absolute bottom (no items below the group)
        (
          lastGroupChild?.id === activeCue?.id &&
          activeCue?.parent_id === overCue?.parent_id &&
          displayedCues[displayedCues.length - 1]?.id === activeCue?.id
        )
      );
    const targetParentId = isLastChildBottomEscape ? null : (overCue?.parent_id ?? null);
    const { updateCue } = useShowStore.getState();

    let cuesToUpdateParent = [];
    const isMultiReparent = !isGroupDrag && selectedIds.size > 1 && selectedIds.has(active.id);
    if (!isGroupDrag) {
      if (isMultiReparent) {
        // Multi-select: update all selected, VISIBLE non-GROUP cues whose
        // parent_id differs — never cues hidden in a collapsed group.
        cuesToUpdateParent = displayedCues.filter(c =>
          selectedIds.has(c.id) &&
          c.cue_type !== 'GROUP' &&
          (c.parent_id ?? null) !== targetParentId
        );
      } else if (activeCue && (activeCue.parent_id ?? null) !== targetParentId) {
        cuesToUpdateParent = [activeCue];
      }
    }

    // Optimistic updates
    reorderCues(orderedIds);
    cuesToUpdateParent.forEach(c => updateCue({ ...c, parent_id: targetParentId }));

    incrementPendingOps();
    try {
      await cuesAPI.reorder(showId, orderedIds);
      if (cuesToUpdateParent.length > 0) {
        await Promise.all(cuesToUpdateParent.map(c =>
          cuesAPI.update(showId, c.id, { parent_id: targetParentId })
        ));
      }
    } catch (err) {
      console.error('Reorder failed:', err);
      reorderCues(cues.map(c => c.id));
      cuesToUpdateParent.forEach(c => updateCue({ ...c }));
    } finally {
      decrementPendingOps();
    }
    // A multi-select drag that reparented cues completes that action — clear it.
    if (isMultiReparent) onClearSelection?.();
  }, [cues, displayedCues, childrenByParent, showId, reorderCues, updateCue, incrementPendingOps, decrementPendingOps, selectedIds, onClearSelection]);

  const activeCue = activeId ? cues.find(c => c.id === activeId) : null;

  // Select-all state for header checkbox
  const allDisplayedSelected = displayedCues.length > 0 && displayedCues.every(c => selectedIds.has(c.id));
  const someDisplayedSelected = !allDisplayedSelected && displayedCues.some(c => selectedIds.has(c.id));

  // Label column default width: fit the longest LINE, not the longest label.
  // Labels can be multiline, and measuring the whole string would let one
  // three-line label widen the column as if it were a single long line.
  const labelColWidth = React.useMemo(() => {
    const longestLine = Math.max(
      5,
      ...cues.flatMap(c => String(c.label || '').split('\n').map(l => l.length))
    );
    return Math.min(Math.max(longestLine * 7.5, 100), 380);
  }, [cues]);

  // Effective column widths
  const colWidths = React.useMemo(() => {
    // Fixed cols always use their exact defaultWidth — no resizing, no auto-fill
    const fixedWidths = Object.fromEntries(
      COLUMN_DEFS.filter(c => c.fixed).map(c => [c.key, c.defaultWidth])
    );

    // Label: use custom width if set, else auto-fit from content
    const labelW = customWidths?.label ?? labelColWidth;

    // Resizable (non-fixed) cols
    const resizableDefs = COLUMN_DEFS.filter(c => !c.fixed);

    if (customWidths) {
      const resizable = Object.fromEntries(
        resizableDefs.map(c => [c.key, customWidths[c.key] || c.defaultWidth])
      );
      return { ...fixedWidths, label: labelW, ...resizable };
    }

    if (containerWidth > 100) {
      const visibleFixedTotal = COLUMN_DEFS
        .filter(c => c.fixed && visible[c.key] !== false)
        .reduce((sum, c) => sum + c.defaultWidth, 0);
      const available = Math.max(containerWidth - fixedOverhead(visible) - visibleFixedTotal - labelW, 200);
      const visibleResizable = resizableDefs.filter(c => visible[c.key] !== false);
      const totalDefault = visibleResizable.reduce((sum, c) => sum + c.defaultWidth, 0);
      const resizable = {};
      resizableDefs.forEach(c => {
        if (visible[c.key] === false) {
          resizable[c.key] = c.defaultWidth;
        } else {
          resizable[c.key] = totalDefault > 0
            ? Math.max(40, Math.round((c.defaultWidth / totalDefault) * available))
            : c.defaultWidth;
        }
      });
      return { ...fixedWidths, label: labelW, ...resizable };
    }

    const resizable = Object.fromEntries(resizableDefs.map(c => [c.key, c.defaultWidth]));
    return { ...fixedWidths, label: labelColWidth, ...resizable };
  }, [customWidths, containerWidth, labelColWidth, visible]);

  // Virtualiser padding — creates the illusion of a full-height list while only
  // mounting rows near the viewport. paddingTop/Bottom replace the unmounted rows.
  const virtualItems = virtualizer.getVirtualItems();
  const paddingTop = virtualItems.length > 0 ? (virtualItems[0].start ?? 0) : 0;
  const paddingBottom = virtualItems.length > 0
    ? virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1].end ?? 0)
    : 0;

  // §6c cue count for the header band — only cues AFTER the current on-air
  // position, so it reads as "what's left to run". Excludes GROUP containers
  // (they aggregate children) and already complete/skipped cues.
  const countLabel = React.useMemo(() => {
    const onAirOrder = onAirCue ? onAirCue.order : -Infinity;
    const upcoming = cues.filter(c =>
      c.order > onAirOrder && c.cue_type !== 'GROUP' && c.status !== 'complete' && c.status !== 'skipped'
    );
    const secs = upcoming.reduce((sum, c) => sum + (c.planned_duration || 0), 0);
    return `${upcoming.length} cue${upcoming.length !== 1 ? 's' : ''} · ${fmtRemaining(secs)}`;
  }, [cues, onAirCue]);

  // Close the bottom "+ Add cue" dropdown on outside click
  useEffect(() => {
    if (!bottomAddOpen) return;
    function handler() { setBottomAddOpen(false); }
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [bottomAddOpen]);

  return (
    <div ref={containerRef} className="relative flex flex-col h-full overflow-hidden">

      {/* Table chrome — column menu + collapse-all.
          Pinned to the top-right of the container rather than living in the
          header row, so it costs the columns no width and stays put during
          horizontal scroll. It used to sit in the leading cluster, which meant
          the cluster could never collapse to nothing: the menu button is the
          only way to turn edit controls back on, so hiding it inside the thing
          it hides would have been a one-way door. Out here, the leading cells
          can disappear entirely and the rows start flush left. */}
      <div className="absolute top-0 right-0 z-20 h-10 flex items-center gap-0.5 pl-3 pr-2 bg-app-chrome-sunk border-b border-l border-app-line-chrome rounded-bl">
        <span
          className="max-w-[9rem] truncate normal-case tracking-normal text-[11px] font-normal leading-none"
          style={{ color: 'var(--faint)' }}
          title={countLabel}
        >
          {countLabel}
        </span>

        {/* Collapse-all groups */}
        {!readOnly && (
          <button
            onClick={() => { setExpandedIds(new Set()); collapseAllGroups(); }}
            title="Collapse all rows"
            className="w-6 h-10 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
        )}

        {/* Column toggle */}
        <div className="relative flex-shrink-0" ref={colMenuRef}>
          <button
            onClick={() => setShowColMenu(v => !v)}
            className="w-6 h-10 flex items-center justify-center text-gray-400 hover:text-gray-200 transition-colors"
            title="Show/hide columns"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </button>
          {showColMenu && (
            <div className="absolute right-0 top-full mt-1 bg-app-surface border border-app-border-strong rounded shadow-xl z-30 py-2 min-w-[190px]">
                <p className="px-3 pb-1 text-[10px] text-gray-500 uppercase tracking-wider">Show columns</p>
                {LEADING_TOGGLES.map(col => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 px-3 py-1 hover:bg-app-border cursor-pointer"
                    title={col.title}
                  >
                    <input
                      type="checkbox"
                      checked={visible[col.key] !== false}
                      onChange={() => toggleCol(col.key)}
                      className="w-3 h-3 accent-blue-500 cursor-pointer"
                    />
                    <span className="text-xs text-gray-300 normal-case tracking-normal">{col.label}</span>
                  </label>
                ))}
                <div className="border-t border-app-border my-1.5" />
                {COLUMN_DEFS.map(col => (
                  <label key={col.key} className="flex items-center gap-2 px-3 py-1 hover:bg-app-border cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visible[col.key] !== false}
                      onChange={() => toggleCol(col.key)}
                      className="w-3 h-3 accent-blue-500 cursor-pointer"
                    />
                    <span className="text-xs text-gray-300 normal-case tracking-normal">{col.label}</span>
                  </label>
                ))}
                {customWidths && (
                  <>
                    <div className="border-t border-app-border my-1.5" />
                    <button
                      onClick={() => { resetWidths(); setShowColMenu(false); }}
                      className="w-full text-left px-3 py-1 text-xs text-gray-400 hover:text-gray-200 hover:bg-app-border transition-colors normal-case tracking-normal"
                    >
                      Reset column widths
                    </button>
                  </>
                )}
              </div>
            )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto">
      {/* Column Headers — sticky so they stay visible on vertical scroll, and scroll with the rows horizontally */}
      <div className="sticky top-0 z-10 flex items-center gap-0 px-0 h-10 bg-app-chrome-sunk border-b border-app-line-chrome flex-shrink-0 text-base font-medium text-gray-500 uppercase tracking-wider select-none">

        {/* Leading spacer — must mirror the data row's edit-control cluster or
            every data column shifts: drag(w-8) + expand(w-8) +
            enable/clone/delete(w-20) = w-36 (9rem). Same rem unit as the row; a
            hardcoded px width drifts, because the root font-size is 14px so
            Tailwind's rem widths aren't 16px multiples. Gone entirely when edit
            controls are hidden, so rows start flush left. */}
        {visible.editControls !== false && (
          <div className="w-36 flex-shrink-0 pl-2 pr-0.5" />
        )}

        {/* Checkbox — select all */}
        {visible.editControls !== false && (
          <div className="w-6 flex-shrink-0 flex items-center justify-center">
            {!readOnly && !textOnly && onSelectAll && (
              <input
                type="checkbox"
                checked={allDisplayedSelected}
                ref={el => { if (el) el.indeterminate = someDisplayedSelected; }}
                onChange={onSelectAll}
                className="w-4 h-4 cursor-pointer accent-blue-500"
                title={allDisplayedSelected ? 'Deselect all' : 'Select all'}
                tabIndex={-1}
              />
            )}
          </div>
        )}

        {/* Status — ON AIR / NEXT markers. Sits here rather than at the far
            right: it is the first thing an operator looks for, and 10 columns
            away from the cue it describes it was easy to miss entirely. */}
        {visible.status !== false && (
          <div className="w-20 text-center flex-shrink-0">Status</div>
        )}

        {/* Order number — w-14 to match the data row's order column (CueRow) */}
        <div className="w-14 text-center flex-shrink-0">#</div>

        {/* Colorbar spacer */}
        <div className="w-1 mr-3 flex-shrink-0" />

        {/* LEDs + test btn spacer */}
        <div className="w-24 flex-shrink-0" />

        {/* Columns — Label is injected between 'in' (index 1) and 'duration' (index 2) */}
        {COLUMN_DEFS.map((col, colIdx) => {
          if (visible[col.key] === false) return null;
          // Inject Label header before 'duration' (index 2)
          const labelHeader = colIdx === 2 ? (
            <div key="label" className="relative flex-shrink-0 px-2 overflow-hidden" style={{ width: colWidths.label }}>
              <span className="truncate block">Label</span>
              {!readOnly && <ResizeHandle colKey="label" currentWidth={colWidths.label} onResize={setColWidth} />}
            </div>
          ) : null;
          const colHeader = (
            <div
              key={col.key}
              className={`relative flex-shrink-0 overflow-hidden ${col.align === 'center' ? 'px-1 text-center' : 'px-2'}`}
              style={{ width: colWidths[col.key] }}
            >
              <span className="truncate block">{col.header}</span>
              {!readOnly && !col.fixed && (
                <ResizeHandle colKey={col.key} currentWidth={colWidths[col.key]} onResize={setColWidth} />
              )}
            </div>
          );
          return colIdx === 2 ? <React.Fragment key={col.key}>{labelHeader}{colHeader}</React.Fragment> : colHeader;
        })}
      </div>

      {/* Cue List — min-w-max forces rows to match the header's column-sum width.
           paddingTop/Bottom are the virtual spacers replacing unmounted rows. */}
      <div className="min-w-max" style={{ paddingTop, paddingBottom }}>
        {cues.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-700">
            <p className="mb-3">No cues yet</p>
            {onAddCue && (
              <button onClick={onAddCue} className="text-blue-600 hover:text-blue-500 text-sm transition-colors">
                + Add first cue
              </button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={cursorAwareCollision}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            {/* SortableContext always receives the full ID list — the virtualiser
                only controls which CueRows are actually mounted */}
            <SortableContext items={displayedCues.map(c => c.id)} strategy={verticalListSortingStrategy}>
              {virtualItems.map(virtualRow => {
                const cue = displayedCues[virtualRow.index];
                if (!cue) return null;
                const displayIndex = virtualRow.index;
                const globalIndex = cues.findIndex(c => c.id === cue.id);
                const isGroupParent = cue.cue_type === 'GROUP';
                const isChild = !!cue.parent_id;
                const isGroupCollapsed = collapsedGroups.has(cue.id);
                const childrenCount = isGroupParent ? (childrenByParent.get(cue.id) || []).length : 0;
                const activeDragCue = activeId ? cues.find(c => c.id === activeId) : null;
                // Freeze every GROUP row from the moment a non-GROUP drag begins,
                // so the row never slips out from under the cursor. Without this
                // flag the sortable strategy animates the GROUP up/down as the
                // cursor approaches, and the freeze only kicks in after the
                // cursor reaches the middle zone — leaving an awkward "slip
                // out, then snap back" feel before the drop-target highlight
                // appears.
                const isContainerLocked = isGroupParent &&
                  activeId != null &&
                  activeId !== cue.id &&
                  activeDragCue?.cue_type !== 'GROUP';
                const isDropTarget = isGroupParent &&
                  activeId != null &&
                  overId === cue.id &&
                  overZone === 'middle' &&
                  activeDragCue?.cue_type !== 'GROUP' &&
                  (selectedIds.size > 1 && selectedIds.has(activeId)
                    ? cues.some(c => selectedIds.has(c.id) && c.cue_type !== 'GROUP' && c.parent_id !== cue.id)
                    : activeDragCue?.parent_id !== cue.id);
                const isCollapsing = isChild && collapsingGroups.has(cue.parent_id);
                const isExpanding = isChild && expandingGroups.has(cue.parent_id);
                return (
                  // Measured wrapper: data-index + measureElement let the
                  // virtualiser track each row's REAL height (including an
                  // expanded actions panel), so a tall expanded row stays
                  // mounted while it's on screen instead of being unmounted —
                  // and collapsed — partway through a scroll.
                  <div key={cue.id} data-index={virtualRow.index} ref={virtualizer.measureElement} className="relative group/ins">
                    <CueRow
                      cue={cue}
                      showId={showId}
                      index={globalIndex}
                      displayIndex={displayIndex}
                      displayNumber={cueNumbers.get(cue.id)}
                      isOnAir={cue.id === onAirCue?.id}
                      isNext={cue.id === nextCue?.id}
                      readOnly={readOnly}
                      textOnly={textOnly}
                      inTime={inTimes[globalIndex]}
                      labelColWidth={colWidths.label}
                      isSelected={selectedIds.has(cue.id)}
                      onToggleSelect={onToggleSelect}
                      isPreShow={showStartIdx !== -1 && globalIndex < showStartIdx}
                      isShowStart={cue.cue_type === 'SHOW_START'}
                      onDeleteSelected={onDeleteSelected}
                      visibleCols={visible}
                      colWidths={colWidths}
                      isExpanded={expandedIds.has(cue.id)}
                      onToggleExpand={toggleExpand}
                      isGroupParent={isGroupParent}
                      isChild={isChild}
                      isGroupCollapsed={isGroupCollapsed}
                      onToggleGroupCollapse={toggleGroupCollapse}
                      childrenCount={childrenCount}
                      isDropTarget={isDropTarget}
                      isContainerLocked={isContainerLocked}
                      groupDuration={isGroupParent ? (groupDurations.get(cue.id) ?? 0) : undefined}
                      isCollapsing={isCollapsing}
                      isExpanding={isExpanding}
                    />
                    {/* §6a row-level insert — hover-only hairline + centred plus
                        in the gap below this row. Inserts a cue at this exact
                        position. Absolutely positioned so it never changes the
                        virtualiser's measured row height. */}
                    {!readOnly && !textOnly && (
                      <div
                        className="pointer-events-none absolute inset-x-0 z-20 flex items-center opacity-0 group-hover/ins:opacity-100 transition-opacity"
                        style={{ bottom: -8, height: 16 }}
                      >
                        <div className="absolute inset-x-0 top-1/2 h-px" style={{ background: 'rgb(var(--c-line-chrome))' }} />
                        <div className="sticky left-0 flex justify-center" style={{ width: containerWidth || '100%' }}>
                          <button
                            onClick={() => onInsertAfter?.(cue)}
                            title="Insert cue here"
                            className="pointer-events-auto relative flex items-center justify-center rounded-full transition-colors"
                            style={{ width: 18, height: 18, background: 'rgb(var(--c-panel))', border: '1px solid rgb(var(--c-line-chrome))', color: 'var(--dim)' }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </SortableContext>

            <DragOverlay>
              {activeCue && (() => {
                // When the cursor is targeting a GROUP middle zone, fade the
                // overlay chip so the (much louder) "ADD TO GROUP" highlight
                // on the row underneath becomes the dominant visual signal —
                // the chip just confirms what's being dropped, not where.
                const overGroup = overZone === 'middle'
                  && cues.find(c => c.id === overId)?.cue_type === 'GROUP'
                  && activeCue.cue_type !== 'GROUP';
                return (
                <div className={`bg-app-surface border border-blue-600 rounded shadow-2xl px-4 py-2 text-sm text-app-fg flex items-center gap-2 transition-opacity duration-100 ${overGroup ? 'opacity-30' : 'opacity-90'}`}>
                  <span>{activeCue.label}</span>
                  {activeCue.cue_type === 'GROUP' && (childrenByParent.get(activeCue.id) || []).length > 0 && (
                    <span className="text-xs text-amber-400 bg-amber-900/40 px-1.5 py-0.5 rounded">
                      +{(childrenByParent.get(activeCue.id) || []).length} cues
                    </span>
                  )}
                  {selectedIds.size > 1 && selectedIds.has(activeCue.id) && (
                    <span className="text-xs text-blue-400 bg-blue-900/40 px-1.5 py-0.5 rounded">
                      {selectedIds.size} selected
                    </span>
                  )}
                </div>
                );
              })()}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* §6b bottom add row — persistent, styled as part of the list (not chrome).
          sticky left-0 keeps both controls visible while the table scrolls
          horizontally. */}
      {cues.length > 0 && !readOnly && !textOnly && onAddCue && (
        <div className="sticky left-0" style={{ width: containerWidth || '100%' }}>
          <div className="flex items-center justify-between h-11 px-4 bg-app-well border-t border-app-line hover:bg-app-row-hover transition-colors">
            <div className="relative" onMouseDown={e => e.stopPropagation()}>
              <div className="flex items-center gap-1">
                <button
                  onClick={onAddCue}
                  className="text-[13px] transition-colors hover:brightness-125"
                  style={{ color: 'var(--dim)' }}
                  title="Add cue to end (N)"
                >
                  + Add cue
                </button>
                <button
                  onClick={() => setBottomAddOpen(v => !v)}
                  className="flex items-center px-1 text-gray-600 hover:text-gray-400 transition-colors"
                  title="More insert options"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>
              {bottomAddOpen && (
                <div className="absolute left-0 bottom-full mb-1 bg-app-panel border border-app-border-strong rounded shadow-xl z-30 min-w-[190px]" onMouseDown={e => e.stopPropagation()}>
                  <button
                    onClick={() => { setBottomAddOpen(false); onInsertBelowSelection?.(); }}
                    disabled={!canInsertBelow}
                    className="w-full text-left px-3 py-2 text-[13px] text-gray-300 hover:bg-app-border disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
                    title={canInsertBelow ? 'Insert below the selected cue' : 'Select a cue first'}
                  >
                    Insert cue (below selection)
                    {!canInsertBelow && <span className="block text-gray-700 text-[10px]">Select a cue first</span>}
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={onOpenCueBlocks}
              className="text-[13px] transition-colors hover:brightness-125"
              style={{ color: 'var(--dim)' }}
              title="Apply or save a cue block"
            >
              Cue blocks
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
